/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import Joi from 'joi'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { AppValues } from '@/Constants/AppValues'
import { VueRouter } from '@/Constants/VueRouter'

/**************************************************************************************************/
/** @import Helpers/Components                                                                   **/
/**************************************************************************************************/
import { ActionField } from '@/Helpers/Components/DataTable/ActionField'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { TypesHelper } from '@/Helpers/Utils/TypesHelper'

/**************************************************************************************************/
/** @export Namespace @name AppModules                                                           **/
/**************************************************************************************************/
export namespace AppModules
{
	export namespace Collections
	{
		export namespace JoiSchemas
		{
			export namespace CheckListAddons
			{
				const JoiSchemaAddonsLabels =
				{
					_idCheckListAddons: 'Formulario de Evaluación',
					_idAdminCompany:    'Cliente',
					_idStorages:        'Ubicaciones Asociadas',
					attributes:         'Listado de Evaluaciones',
					code:               'Código Formulario',
					groupArticle:       'Grupo Articulo',
					isForClient:        '¿Exclusivo Cliente?',
					name:               'Descripción',
					typeEquipment:      'Tipo de Equipo'
				}

				export const AddCheckListAddon = Joi.object({
					_idAdminCompany: Joi.string().hex().required().label(JoiSchemaAddonsLabels._idAdminCompany),
					_idStorages:     Joi.array().items(Joi.string().hex()).label(JoiSchemaAddonsLabels._idStorages),
					attributes:      Joi.array().items({ name: Joi.string(), critical: Joi.boolean() }).label(JoiSchemaAddonsLabels.attributes),
					code:            Joi.string().required().label(JoiSchemaAddonsLabels.code),
					name:            Joi.string().required().label(JoiSchemaAddonsLabels.name),
					groupArticle:    Joi.string().required().label(JoiSchemaAddonsLabels.groupArticle),
					isForClient:     Joi.boolean().required().label(JoiSchemaAddonsLabels.isForClient),
					typeEquipment:   Joi.string().required().label(JoiSchemaAddonsLabels.typeEquipment)
				})

				export const UpdateCheckListAddon = Joi.object({
					_idCheckListAddons: Joi.string().hex().required().label(JoiSchemaAddonsLabels._idCheckListAddons),
					_idStorages:        Joi.array().items(Joi.string().hex()).label(JoiSchemaAddonsLabels._idStorages),
					attributes:         Joi.array().items({ name: Joi.string(), critical: Joi.boolean() }).label(JoiSchemaAddonsLabels.attributes),
					code:               Joi.string().required().label(JoiSchemaAddonsLabels.code),
					name:               Joi.string().required().label(JoiSchemaAddonsLabels.name),
					groupArticle:       Joi.string().required().label(JoiSchemaAddonsLabels.groupArticle),
					isForClient:        Joi.boolean().required().label(JoiSchemaAddonsLabels.isForClient),
					typeEquipment:      Joi.string().required().label(JoiSchemaAddonsLabels.typeEquipment)
				})
			}

			export namespace CheckSettings
			{
				const JoiSchemaLabels =
				{
					_idAdminCompany: '_idAdminCompany',
					_idCompany:      '_idCompany',
					_idResponsible:  '_idResponsible',
					code:            'Código Interno',
					dateValidation:  'Fecha Vigencia'
				}

				export const UpsertSettingAuditCode = Joi.object({
					_idAdminCompany: Joi.string().hex().required().label(JoiSchemaLabels._idAdminCompany),
					_idCompany:      Joi.string().hex().required().label(JoiSchemaLabels._idCompany),
					_idResponsible:  Joi.string().hex().required().label(JoiSchemaLabels._idResponsible),
					code:            Joi.string().required().label(JoiSchemaLabels.code),
					dateValidation:  Joi.string().required().label(JoiSchemaLabels.dateValidation)
				})
			}

			export namespace Companies
			{
				const JoiSchemaCompanyLabels =
				{
					_idAdminCompany: '_idAdminCompany',
					_idCompany:      '_idCompany',
					code:            'Código',
					rut:             'Rut',
					name:            'Razón Social',
					address:         'Dirección de Facturación',
					region:          'Región',
					commune:         'Comuna',
					type:            'Tipo',
					isClient:        'Es Cliente',
					isValid:         'Esta Activa'
				}

				export const AddCompany = Joi.object({
					_idAdminCompany: Joi.string().required().label(JoiSchemaCompanyLabels._idAdminCompany),
					code:            Joi.string().required().label(JoiSchemaCompanyLabels.code),
					rut:             Joi.string().required().label(JoiSchemaCompanyLabels.rut),
					name:            Joi.string().required().label(JoiSchemaCompanyLabels.name),
					address:         Joi.string().required().label(JoiSchemaCompanyLabels.address),
					region:          Joi.string().required().label(JoiSchemaCompanyLabels.region),
					commune:         Joi.string().required().label(JoiSchemaCompanyLabels.commune),
					type:            Joi.string().required().label(JoiSchemaCompanyLabels.type),
					isClient:        Joi.boolean().required().label(JoiSchemaCompanyLabels.isClient),
				})

				export const UpdateCompany = Joi.object({
					_idCompany: Joi.string().hex().required(),
					name:       Joi.string().required().label(JoiSchemaCompanyLabels.name),
					address:    Joi.string().required().label(JoiSchemaCompanyLabels.address),
					commune:    Joi.string().required().label(JoiSchemaCompanyLabels.commune),
					region:     Joi.string().required().label(JoiSchemaCompanyLabels.region),
					isValid:    Joi.boolean().required().label(JoiSchemaCompanyLabels.isValid)
				})
			}

			export namespace Equipments
			{
				const JoiSchemaEquipmentLabels =
				{
					_idStorage:     'Ubicación',
					code:           'Código',
					hourmeter:      'Horómetro',
					isValid:        'Estado',
					mark:           'Marca',
					name:           'Descripción',
					groupArticle:   'Grupo Artículo',
					groupEquipment: 'Grupo Equipo',
					serie:          'Serie',
					typeArticle:    'Tipo Artículo',
					typeEquipment:  'Tipo Equipo',
					year:           'Año'
				}

				export const AddEquipment = Joi.object({
					_idStorage:     Joi.string().hex().required().label(JoiSchemaEquipmentLabels._idStorage),
					code:           Joi.string().required().label(JoiSchemaEquipmentLabels.code),
					serie:          Joi.string().required().label(JoiSchemaEquipmentLabels.serie),
					name:           Joi.string().required().label(JoiSchemaEquipmentLabels.name),
					mark:           Joi.string().required().allow('').label(JoiSchemaEquipmentLabels.mark),
					groupArticle:   Joi.string().required().label(JoiSchemaEquipmentLabels.groupArticle),
					typeArticle:    Joi.string().required().allow('').label(JoiSchemaEquipmentLabels.typeArticle),
					groupEquipment: Joi.string().required().allow('').label(JoiSchemaEquipmentLabels.groupEquipment),
					typeEquipment:  Joi.string().required().allow('').label(JoiSchemaEquipmentLabels.typeEquipment),
					year:           Joi.number().required().label(JoiSchemaEquipmentLabels.year),
					hourmeter:      Joi.number().allow(0).label(JoiSchemaEquipmentLabels.hourmeter)
				})

				export const UpdateEquipment = Joi.object({
					_idEquipment:   Joi.string().hex().required(),
					_idStorage:     Joi.string().hex().required().label(JoiSchemaEquipmentLabels._idStorage),
					serie:          Joi.string().required().label(JoiSchemaEquipmentLabels.serie),
					name:           Joi.string().required().label(JoiSchemaEquipmentLabels.name),
					mark:           Joi.string().required().allow('').label(JoiSchemaEquipmentLabels.mark),
					groupArticle:   Joi.string().required().label(JoiSchemaEquipmentLabels.groupArticle),
					typeArticle:    Joi.string().required().allow('').label(JoiSchemaEquipmentLabels.typeArticle),
					groupEquipment: Joi.string().required().allow('').label(JoiSchemaEquipmentLabels.groupEquipment),
					typeEquipment:  Joi.string().required().label(JoiSchemaEquipmentLabels.typeEquipment),
					year:           Joi.number().required().label(JoiSchemaEquipmentLabels.year),
					isValid:        Joi.boolean().required().label(JoiSchemaEquipmentLabels.isValid)
				})
			}

			export namespace Installations
			{
				const JoiSchemaInstallationsLabels =
				{
					_idInstallation: 'Formulario de Evaluación',
					_idResponsible:  'Responsable',
					_idStorages:     'Ubicaciones Asociadas',
					code:            'Código Instalación',
					name:            'Descripción',
					isValid:         'IsValid'
				}

				export const AddInstallation = Joi.object({
					_idResponsible: Joi.string().hex().required().label(JoiSchemaInstallationsLabels._idResponsible),
					_idStorages:    Joi.array().items(Joi.string().hex()).min(1).label(JoiSchemaInstallationsLabels._idStorages),
					code:           Joi.string().required().label(JoiSchemaInstallationsLabels.code),
					name:           Joi.string().required().label(JoiSchemaInstallationsLabels.name)
				})

				export const UpdateInstallation = Joi.object({
					_idInstallation: Joi.string().hex().required().label(JoiSchemaInstallationsLabels._idInstallation),
					_idResponsible:  Joi.string().hex().required().label(JoiSchemaInstallationsLabels._idResponsible),
					_idStorages:     Joi.array().items(Joi.string().hex()).min(1).label(JoiSchemaInstallationsLabels._idStorages),
					code:            Joi.string().required().label(JoiSchemaInstallationsLabels.code),
					name:            Joi.string().required().label(JoiSchemaInstallationsLabels.name),
					isValid:         Joi.boolean().required().label(JoiSchemaInstallationsLabels.isValid)
				})
			}

			export namespace Operators
			{
				const JoiSchemaOperatorBodyNames =
				{
					_idStorage:     'Ubicación',
					rut:            'Rut',
					name:           'Nombre',
					pLastName:      '1er Apellido',
					mLastName:      '2do Apellido',
					password:       'Contraseña',
					isValid:        'Estado',
					position:       'Cargo',
					licenses:       'Licencia',
					certifications: 'Certificación',
				}

				export const AddOperator = Joi.object({
					_idStorage:     Joi.string().hex().required().label(JoiSchemaOperatorBodyNames._idStorage),
					certifications: Joi.array(),
					licenses:       Joi.array(),
					mLastName:      Joi.string().allow('').min(3).max(32).label(JoiSchemaOperatorBodyNames.mLastName),
					name:           Joi.string().required().min(3).max(32).label(JoiSchemaOperatorBodyNames.name),
					rut:            Joi.string().required().label(JoiSchemaOperatorBodyNames.rut),
					password:       Joi.string().required().label(JoiSchemaOperatorBodyNames.password),
					pLastName:      Joi.string().required().min(3).max(32).label(JoiSchemaOperatorBodyNames.pLastName),
					position:       Joi.string().required().label(JoiSchemaOperatorBodyNames.position)
				})

				export const UpdateOperator = Joi.object({
					_idOperator:    Joi.string().hex().required(),
					_idStorage:     Joi.string().hex().required().label(JoiSchemaOperatorBodyNames._idStorage),
					certifications: Joi.array(),
					isValid:        Joi.boolean().required().label(JoiSchemaOperatorBodyNames.isValid),
					licenses:       Joi.array(),
					mLastName:      Joi.string().allow('').min(3).max(32),
					name:           Joi.string().required().min(3).max(32).label(JoiSchemaOperatorBodyNames.name),
					password:       Joi.string(),
					pLastName:      Joi.string().required().min(3).max(32).label(JoiSchemaOperatorBodyNames.pLastName),
					position:       Joi.string().required().label(JoiSchemaOperatorBodyNames.position)
				})
			}

			export namespace Roles
			{
				const JoiSchemaRoleLabels =
				{
					_idAdminCompany: '_idAdminCompany',
					isValid:         'Esta Activo',
					name:            'Rol',
					permissions:     'Permisos'
				}

				export const AddRole = Joi.object({
					_idAdminCompany: Joi.string().hex().required().label(JoiSchemaRoleLabels._idAdminCompany),
					name:            Joi.string().required().label(JoiSchemaRoleLabels.name),
					permissions:     Joi.array().required().label(JoiSchemaRoleLabels.permissions)
				})

				export const UpdateRole = Joi.object({
					_idRole:     Joi.string().hex().required(),
					name:        Joi.string().required().label(JoiSchemaRoleLabels.name),
					isValid:     Joi.boolean().required().label(JoiSchemaRoleLabels.isValid),
					permissions: Joi.array().required().label(JoiSchemaRoleLabels.permissions)
				})
			}

			export namespace Services
			{
				const JoiSchemaLabels =
				{
					_idClient:       'ID Cliente',
					_idEquipment:    'Equipo',
					_idService:      'ID Servicio',
					_idStorage:      'ID Planta',
					_idUser:         'ID Usuario',
					dateProgram:     'Fecha Reparación',
					description:     'Descripción del Servicio',
					details:         'Detalle del Servicio',
					reasonReject:    'Motivo Rechazo',
					status:          'Estado del Servicio',
					statusEquipment: 'Estatus Equipo',
					technicalName:   'Reasignar Técnico',
					workingDay:      'Jornada'
				}

				export const AddService = Joi.object({
					_idClient:		 Joi.string().hex().required().label(JoiSchemaLabels._idClient),
					_idEquipment:    Joi.string().hex().required().label(JoiSchemaLabels._idEquipment),
					_idStorage:      Joi.string().hex().required().label(JoiSchemaLabels._idStorage),
					description:     Joi.string().required().label(JoiSchemaLabels.description),
					details:         Joi.string().required().label(JoiSchemaLabels.details),
					statusEquipment: Joi.string().required().label(JoiSchemaLabels.statusEquipment)
				})

				export const UpdateService = (isBeingAccepted: boolean) =>
				{
					// Validaciones Especificas.
					let joiDateProgram = Joi.string().label(JoiSchemaLabels.dateProgram)
					let joiWorkingDay  = Joi.string().label(JoiSchemaLabels.workingDay)

					// Para el caso del Servicio que esta siendo Aceptado, eliminar validaciones que no corresponden al caso.
					if (isBeingAccepted)
					{
						joiDateProgram = joiDateProgram.required()
						joiWorkingDay  = joiWorkingDay.required()
					}

					// De lo contario, se da por hecho que el Servicio esta siendo Rechazado.
					else
					{
						joiDateProgram = joiDateProgram.allow('')
						joiWorkingDay  = joiWorkingDay.allow('')
					}

					const joi: Record<string, Joi.StringSchema> =
					{
						_idService:		 Joi.string().hex().required().label(JoiSchemaLabels._idService),
						_idTechnical:    Joi.string().hex().label(JoiSchemaLabels.technicalName),
						_idUser:		 Joi.string().hex().required().label(JoiSchemaLabels._idUser),
						dateProgram:     joiDateProgram,
						rasonRejection:  Joi.string().allow(null, '').label(JoiSchemaLabels.reasonReject),
						statusEquipment: Joi.string().required().allow('').label(JoiSchemaLabels.statusEquipment),
						status: 		 Joi.string().allow('').label(JoiSchemaLabels.status),
						workingDay:      joiWorkingDay
					}

					return Joi.object(joi)
				}
			}

			export namespace Storages
			{
				const JoiSchemaStorageLabels =
				{
					_idCompany:   '_idCompany',
					_idTechnical: '_idTechnical',
					_idUserAdmin: '_idUserAdmin',
					codeLocation: 'codeLocation',
					code:         'Código',
					name:         'Nombre',
					address:      'Dirección',
					region:       'Región',
					commune:      'Comuna'
				}

				export const AddStorage = Joi.object({
					_idCompany:   Joi.string().hex().required().label(JoiSchemaStorageLabels._idCompany),
					_idTechnical: Joi.string().hex().allow(null).label(JoiSchemaStorageLabels._idTechnical),
					_idUserAdmin: Joi.string().hex().allow(null).label(JoiSchemaStorageLabels._idUserAdmin),
					codeLocation: Joi.string().required().label(JoiSchemaStorageLabels.codeLocation),
					code:         Joi.string().required().label(JoiSchemaStorageLabels.code),
					name:         Joi.string().required().label(JoiSchemaStorageLabels.name),
					address:      Joi.string().min(4).max(255).required().label(JoiSchemaStorageLabels.address),
					commune:      Joi.string().required().label(JoiSchemaStorageLabels.commune),
					region:       Joi.string().required().label(JoiSchemaStorageLabels.region)
				})

				export const UpdateStorage = Joi.object({
					_idStorage:   Joi.string().hex().required(),
					_idCompany:   Joi.string().hex().required().label(JoiSchemaStorageLabels._idCompany),
					_idTechnical: Joi.string().hex().allow(null).label(JoiSchemaStorageLabels._idTechnical),
					_idUserAdmin: Joi.string().hex().allow(null).label(JoiSchemaStorageLabels._idUserAdmin),
					code:         Joi.string().required().label(JoiSchemaStorageLabels.code),
					name:         Joi.string().required().label(JoiSchemaStorageLabels.name),
					address:      Joi.string().min(4).max(255).required().label(JoiSchemaStorageLabels.address),
					region:       Joi.string().required().label(JoiSchemaStorageLabels.region),
					commune:      Joi.string().required().label(JoiSchemaStorageLabels.commune),
					isValid:      Joi.boolean().required()
				})
			}

			export namespace Users
			{
				const JoiSchemaLabels =
				{
					_idAdminCompany: '_idAdminCompany',
					_idCompany:  'Empresa',
					_idRole:     'Rol',
					_idWorkArea: 'Área de Trabajo',
					rut:         'Rut',
					name:        'Nombre',
					pLastName:   'Apellido Paterno',
					mLastName:   'Apellido Materno',
					email:       'Correo',
					phone:       'Fono',
					password:    'Contraseña',
					storages:    'Ubicaciones',
					permissions: 'Permisos'
				}

				export const AddUser = Joi.object({
					_idAdminCompany: Joi.string().required().label(JoiSchemaLabels._idAdminCompany),
					_idCompany:  Joi.string().hex().required().label(JoiSchemaLabels._idCompany),
					_idRole:     Joi.string().hex().required().label(JoiSchemaLabels._idRole),
					_idWorkArea: Joi.string().hex().required().label(JoiSchemaLabels._idWorkArea),
					rut:         Joi.string().min(9).max(12).label(JoiSchemaLabels.rut),
					name:        Joi.string().label(JoiSchemaLabels.name),
					pLastName:   Joi.string().required().label(JoiSchemaLabels.pLastName),
					mLastName:   Joi.string().allow(''),
					email:       Joi.string().email({ tlds:{ allow: false } }).required().label(JoiSchemaLabels.email),
					phone:       Joi.array(),
					password:    Joi.string().required().label(JoiSchemaLabels.password),
					storages:    Joi.array().items(Joi.string().hex()),
					permissions: Joi.array()
				})

				export const UpdateUser = Joi.object({
					_idUser:     Joi.string().hex().required(),
					_idRole:     Joi.string().hex().required().label(JoiSchemaLabels._idRole),
					_idWorkArea: Joi.string().hex().required().label(JoiSchemaLabels._idWorkArea),
					rut:         Joi.string().min(9).max(12).label(JoiSchemaLabels.rut),
					name:        Joi.string().label(JoiSchemaLabels.name),
					pLastName:   Joi.string().label(JoiSchemaLabels.pLastName),
					mLastName:   Joi.string().allow(''),
					email:       Joi.string().email({ tlds:{ allow: false } }).label(JoiSchemaLabels.email),
					phone:       Joi.array(),
					storages:    Joi.array().items(Joi.string().hex()),
					isValid:      Joi.boolean().required(),
					permissions: Joi.array()
				})
			}

			export namespace WorkAreas
			{
				const JoiSchemaWorkAreaLabels =
				{
					_idAdminCompany: '_idAdminCompany',
					_idLeader:       'Líder',
					isValid:         'Esta Activa',
					name:            'Nombre'
				}

				export const AddWorkArea = Joi.object({
					_idAdminCompany: Joi.string().hex().required().label(JoiSchemaWorkAreaLabels._idAdminCompany),
					_idLeader:       Joi.string().hex().required().label(JoiSchemaWorkAreaLabels._idLeader),
					name:            Joi.string().required().label(JoiSchemaWorkAreaLabels.name)
				})

				export const UpdateWorkArea = Joi.object({
					_idWorkArea: Joi.string().required(),
					_idLeader:   Joi.string().hex().label(JoiSchemaWorkAreaLabels._idLeader),
					name:        Joi.string().required().label(JoiSchemaWorkAreaLabels.name),
					isValid:     Joi.boolean().required().label(JoiSchemaWorkAreaLabels.isValid)
				})
			}

			export namespace Zones
			{
				const JoiSchemaInternalStorageBodyNames =
				{
					_idCreator: '_idCreator',
					_idStorage: '_idStorage',
					code:       'Código Ubicación',
					equipments: 'Equipos Asignados',
					name:       'Descripción Ubicación'
				}

				export const AddInternalStorage = Joi.object({
					_idCreator: Joi.string().hex().required().label(JoiSchemaInternalStorageBodyNames._idCreator),
					_idStorage: Joi.string().hex().required().label(JoiSchemaInternalStorageBodyNames._idStorage),
					code:       Joi.string().required().label(JoiSchemaInternalStorageBodyNames.code),
					equipments: Joi.array().items(Joi.string().hex()).min(0).required().label(JoiSchemaInternalStorageBodyNames.equipments),
					name:       Joi.string().required().label(JoiSchemaInternalStorageBodyNames.name)
				})

				export const UpdateEquipmentZone = Joi.object({
					_idEquipment: Joi.string().hex().required(),
					_idZone:      Joi.string().hex().required(),
				})

				export const UpdateInternalStorage = Joi.object({
					_idZone:    Joi.string().hex().required(),
					equipments: Joi.array().items(Joi.string().hex()).min(0).required().label(JoiSchemaInternalStorageBodyNames.equipments),
					isValid:    Joi.boolean().required(),
					name:       Joi.string().required().label(JoiSchemaInternalStorageBodyNames.name)
				})
			}
		}
	}

	export namespace Components
	{
		export namespace MenuBar
		{
			enum AddonsSetupKeyOptionsEnum
			{
				EVALUATIONS,
				STORAGES,
			}

			export const AddonsSetupItemOptions = [
				{
					key: AddonsSetupKeyOptionsEnum.EVALUATIONS,
					icon: 'list', label: 'Evaluaciones'
				},
				{
					key: AddonsSetupKeyOptionsEnum.STORAGES,
					icon: 'list', label: 'Plantas'
				}
			]

			export const AddonsSetupKeyOptions =
			{
				...AddonsSetupKeyOptionsEnum
			}

			enum CheckListsKeyOptionsEnum
			{
				ACTIVES,
				ADDONS,
				CHECKLISTS,
				KPI_CHARTS,
				KPI_OPERATORS,
				SETTING_AUDIT_CODE
			}

			export const CheckListsItemOptions = (shortened: boolean) => [
				{
					key: CheckListsKeyOptionsEnum.ACTIVES,
					icon: 'truck-pickup', label: shortened ? 'Actual' : 'CheckList Actual'
				},
				{
					key: CheckListsKeyOptionsEnum.CHECKLISTS,
					icon: 'list', label: 'Historico'
				},
				{
					key: CheckListsKeyOptionsEnum.ADDONS,
					icon: 'clipboard-check', label: 'Formularios'
				},
				{
					categoryIcon: 'chart-pie', categoryLabel: 'KPI\'s', categoryOpened: false,
					items: [
						{
							key: CheckListsKeyOptionsEnum.KPI_CHARTS,
							icon: 'chart-pie', label: 'CheckLists'
						},
						{
							key: CheckListsKeyOptionsEnum.KPI_OPERATORS,
							icon: 'chart-pie', label: 'Operarios'
						}
					]
				},
				{
					categoryIcon: 'cogs', categoryLabel: shortened ? 'Config.' : 'Configuraciones', categoryOpened: false,
					items: [
						{
							key: CheckListsKeyOptionsEnum.SETTING_AUDIT_CODE,
							icon: 'cogs', label: shortened ? 'Cód. Auditoria' : 'Código Auditoria'
						}
					]
				}
			]

			export const CheckListsKeyOptions =
			{
				...CheckListsKeyOptionsEnum
			}

			enum ServicesKeyOptionsEnum
			{
				ALL_SERVICES,
				USER_SERVICES
			}

			export const ServicesItemOptions = (shortened: boolean) => [
				{
					key: ServicesKeyOptionsEnum.USER_SERVICES,
					icon: 'user', label: 'Asignados'
				},
				{
					key: ServicesKeyOptionsEnum.ALL_SERVICES,
					icon: 'users', label: 'Todas las Solicitudes'
				}
			]

			export const ServicesKeyOptions =
			{
				...ServicesKeyOptionsEnum
			}

			enum StatisticsKeyOptionsEnum
			{
				ACTIVES_PER_CONTRACT,
				BILLING_PER_MONTH,
				CORRECTIVE_MAINTENANCE,
				EQUIPMENTS_USAGE,
				PREVENTIVE_MAINTENANCE,
			}

			export const StatisticsItemOptions = (shortened: boolean, isClient: boolean) =>
			{
				const items = []
				items.push(
				{
					key: StatisticsKeyOptionsEnum.ACTIVES_PER_CONTRACT,
					icon: 'truck-pickup', label: shortened ? 'Equipos Por Contrato' : 'Equipos Por Contrato'
				})
				if (!isClient)
				{
					items.push({
						key: StatisticsKeyOptionsEnum.PREVENTIVE_MAINTENANCE,
						icon: 'truck-pickup', label: 'Mantenimiento Preventivo'
					})
				}
				items.push({
					key: StatisticsKeyOptionsEnum.CORRECTIVE_MAINTENANCE,
					icon: 'truck-pickup', label: 'Mantenimiento Correctivo'
				},
				{
					key: StatisticsKeyOptionsEnum.BILLING_PER_MONTH,
					icon: 'clipboard-check', label: isClient ? 'Facturación' : 'Facturación y horas'
				},
				{
					key: StatisticsKeyOptionsEnum.EQUIPMENTS_USAGE,
					icon: 'clipboard-check', label: isClient ? 'Utilización/Disponibilidad' : 'Utilización'
				})
				return items
			}

			export const StatisticsKeyOptions =
			{
				...StatisticsKeyOptionsEnum
			}

			enum SetupCheckListAssociationKeyOptionsEnum
			{
				CODE,
				STORAGES,
				EVERYTHING
			}

			export const SetupCheckListAssociationItemOptions = [
				{
					key: SetupCheckListAssociationKeyOptionsEnum.CODE,
					icon: 'list', label: 'Código'
				},
				{
					key: SetupCheckListAssociationKeyOptionsEnum.STORAGES,
					icon: 'list', label: 'Plantas'
				},
				{
					key: SetupCheckListAssociationKeyOptionsEnum.EVERYTHING,
					icon: 'list', label: 'Todo'
				}
			]

			export const SetupCheckListAssociationKeyOptions =
			{
				...SetupCheckListAssociationKeyOptionsEnum
			}

			enum ZonesManagerKeyOptionsEnum
			{
				INTERNAL_STORAGE,
				INTERNAL_ASSIGNMENT
			}

			export const ZonesManagerItemOptions = [
				{
					key: ZonesManagerKeyOptionsEnum.INTERNAL_STORAGE,
					icon: 'map-marker-alt', label: 'Ubicaciones'
				},
				{
					key: ZonesManagerKeyOptionsEnum.INTERNAL_ASSIGNMENT,
					icon: 'list', label: 'Asignación'
				}
			]

			export const ZonesManagerKeyOptions =
			{
				...ZonesManagerKeyOptionsEnum
			}
		}

		export namespace SideMenu
		{
			export const SetupCheckListItems =
			{
				items: [
					{ type: 'button', label: 'Formularios', componentName: 'Addons', icon: 'building', isActive: true, items: [
						{ name: 'Clients', label: 'Formularios Cliente', isActive: true },
						{ name: 'ByDefault', label: 'Formularios por Defecto', isActive: false },
						{ name: 'ByStorage', label: 'Asignación por Planta', isActive: false }
					] },
					{ type: 'button', label: 'Instalaciones', componentName: 'Facilities', icon: 'warehouse', isActive: false, items: [
						{ name: 'List', label: 'Listado de Instalaciones', isActive: false },
						{ name: 'ByStorage', label: 'Asignación por Planta', isActive: false }
					] }
				]
			}
		}
	}

	export namespace Global
	{
		export const ExternalModulesList =
		{
			[AppValues.ObjectIds.Modules.SERVICES]: {
				...TypesHelper.newObjectModuleStructure(1, true, 'Solicitudes de Servicio', 'ModuleServices.png'),
				description: 'Gestión de solicitudes de atención, visualización de seguimiento & reasignaciones.',
				path: VueRouter.Views.Modules.SERVICES_CPANEL.NAME, props: { moduleEnvironment: 'External' }
			},
			[AppValues.ObjectIds.Modules.CHECKLISTS]: {
				...TypesHelper.newObjectModuleStructure(2, true, 'CheckLists', 'ModuleCheckList.png'),
				description: 'Inspecciones de equipos e instalaciones, visualización de formularios & indicadores KPI.',
				path: VueRouter.Views.Modules.CheckList.MainView.NAME, props: { moduleEnvironment: 'External' }
			},
			[AppValues.ObjectIds.Modules.STATISTICS]: {
				...TypesHelper.newObjectModuleStructure(3, true, 'Estadísticas', 'ModuleStatistics.png'),
				description: 'Modulo de Estadísticas, KPIs, gestión de informes, graficos y analisis estadístico.',
				path: VueRouter.Views.Modules.STATISTICS_CPANEL.NAME, props: { moduleEnvironment: 'External' }
			}
		} as ModulesList

		export const SidePanelExternalItems = [
			{ action: VueRouter.Views.Modules.EXTERNAL_CPANEL.NAME, label: 'Listado Modulos', icon: 'dice-d6', selected: true },
			{ action: VueRouter.Views.Modules.OPERATORS_MANAGER.NAME, label: 'Gestión Operadores', icon: 'users-cog', selected: false },
			{ action: VueRouter.Views.Modules.ZONES_MANAGER.NAME, label: 'Gestión Ubicaciones Internas', icon: 'users-cog', selected: false }
		]

		export const InternalModulesList =
		{
			[AppValues.ObjectIds.Modules.ADMINISTRATION]: {
				...TypesHelper.newObjectModuleStructure(1, true, 'Administración', 'ModuleAdministration.png'),
				description: 'Gestión de clientes, usuarios, roles, áreas de trabajo & visualización de registros (Plantas, Equipos, etc).',
				path: VueRouter.Views.Modules.Administration.ExternalUsersView.NAME, props: { moduleEnvironment: 'Internal' }
			},
			[AppValues.ObjectIds.Modules.SERVICES]: {
				...TypesHelper.newObjectModuleStructure(2, true, 'Solicitudes de Servicio', 'ModuleServices.png'),
				description: 'Gestión de solicitudes de atención, visualización de seguimiento & reasignaciones.',
				path: VueRouter.Views.Modules.SERVICES_CPANEL.NAME, props: { moduleEnvironment: 'Internal' }
			},
			[AppValues.ObjectIds.Modules.CHECKLISTS]: {
				...TypesHelper.newObjectModuleStructure(3, true, 'CheckLists', 'ModuleCheckList.png'),
				description: 'Inspecciones de equipos e instalaciones, visualización de formularios & indicadores KPI.',
				path: VueRouter.Views.Modules.CheckList.MainView.NAME, props: { moduleEnvironment: 'Internal' }
			},
			[AppValues.ObjectIds.Modules.SETUP_CHECKLIST]: {
				...TypesHelper.newObjectModuleStructure(4, true, 'Setup CheckList', 'ModuleSetupCheckList.png'),
				description: 'Gestión de formularios CheckList e instalaciones, asignación de registros por plantas.',
				path: VueRouter.Views.Modules.CheckList.SetupView.NAME, props: { moduleEnvironment: 'Internal' }
			},
			[AppValues.ObjectIds.Modules.STATISTICS]: {
				...TypesHelper.newObjectModuleStructure(5, true, 'Estadísticas', 'ModuleStatistics.png'),
				description: 'Modulo de Estadísticas, KPIs, gestión de informes, graficos y analisis estadístico.',
				path: VueRouter.Views.Modules.STATISTICS_CPANEL.NAME, props: { moduleEnvironment: 'Internal' }
			},
			[AppValues.ObjectIds.Modules.MANAGE_CHECKLIST]: {
				...TypesHelper.newObjectModuleStructure(6, true, 'Gestión CheckList', 'ModuleSetupCheckList.png'),
				description: 'Gestión de registros CheckList.',
				path: VueRouter.Views.Modules.CheckList.ManageView.NAME, props: { moduleEnvironment: 'Internal' }
			}
		} as ModulesList
	}

	export namespace Modules
	{
		export namespace CheckLists
		{
			export interface ChartOptionFilters
			{
				asPercentage?: boolean
				group?: AppValues.PeriodsGroupList
				period?: AppValues.PeriodsChoiceList
			}

			export enum SocketEvents
			{
				CREATED = 'CheckListCreated'
			}

			export enum TypeActiveNames
			{
				FACILITY = 'Facility'
			}

			export enum TypeActiveTranslationNames
			{
				FACILITY = 'Instalación'
			}
		}

		export namespace Services
		{
			export namespace ActionFields
			{
				const TempColorStates: { [key: string]: string } =
				{
					Abierto:    'blue',
					Rechazado:  'red',
					'En Curso': 'orange',
					Finalizado: 'green',
				}

				export const ActionFieldStatus = new ActionField('mStatus', 'Estado Solicitud')
					.addItem('state', 'button')
					.setColor('status', TempColorStates)
					.setPermission('STATUS_BUTTON')
					.setTextFrom('status')
				;

				export const Services = [
					ActionFieldStatus,
					new ActionField('actions', 'Acciones')
						.addItem('edit', 'icon')
							.setIcon('pen')
							.setPermission('ACTION_EDIT')
							.setTitle('Editar Servicio')
							.setVariant('green')
						// ;
					// ;
				]
			}

			export enum SocketEvents
			{
				CREATED = 'ServiceCreated',
				UPDATED = 'ServiceUpdated'
			}

			export enum Status
			{
				ACCEPTED = 'En Curso',
				REJECTED = 'Rechazado'
			}
		}

		export namespace Statistics
		{
			export namespace ActionFields
			{
				const CorrectiveMaintenanceColorStatus: { [key: string]: string } =
				{
					'Abierto': 'yellow',
					'Cerrado': 'green',
				}

				export const CorrectiveMaintenancePerActive = [
					new ActionField('mStatus', 'Estado')
						.addItem('state', 'button')
						.setColor('mantStatus', CorrectiveMaintenanceColorStatus)
						.setPermission('STATUS_BUTTON')
						.setTextFrom('mantStatus')
					,
					new ActionField('actions', 'Acciones')
						.addItem('edit', 'icon')
							.setIcon('pen')
							.setPermission('ACTION_EDIT')
							.setTitle('Revisar Mantenciones')
							.setVariant('green')
						// ;
					// ;
				]

				const PreventiveMaintenanceColorStatus: { [key: string]: string } =
				{
					'Por Hacer':        'gray',
					'Próximo a Vencer': 'yellow',
					'Vencido':          'red',
					'Vigente':          'green',
				}

				export const MaintenancePerActive = [
					new ActionField('mStatus', 'Estado')
						.addItem('state', 'button')
						.setColor('statusMant', PreventiveMaintenanceColorStatus)
						.setPermission('STATUS_BUTTON')
						.setTextFrom('statusMant')
					,
					new ActionField('actions', 'Acciones')
						.addItem('edit', 'icon')
							.setIcon('pen')
							.setPermission('ACTION_EDIT')
							.setTitle('Revisar Mantenciones')
							.setVariant('green')
						// ;
					// ;
				]
			}
		}
	}
}

/**************************************************************************************************/
/** @export Interface @name ModulesList                                                          **/
/**************************************************************************************************/
export interface ModulesList
{
	[key: string]: ModuleDefinition
}

/**************************************************************************************************/
/** @export Interface @name ModuleDefinition                                                     **/
/**************************************************************************************************/
export interface ModuleDefinition
{
	_params: ModuleParamsDefinition
	description: string
	icon: string
	props?: ModulePropsDefinition
	path: string
	title: string
}

/**************************************************************************************************/
/** @internal Interface @name ModuleParamsDefinition                                             **/
/**************************************************************************************************/
interface ModuleParamsDefinition
{
	[key: string]: boolean | number
	index: number
	selectable: boolean
	visible: boolean
}

/**************************************************************************************************/
/** @internal Interface @name ModulePropsDefinition                                              **/
/**************************************************************************************************/
interface ModulePropsDefinition
{
	moduleEnvironment?: 'External' | 'Internal'
}